import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  ChartBarSquareIcon,
  GiftIcon,
  HomeIcon,
  LifebuoyIcon,
  RectangleStackIcon,
  TicketIcon,
  UserCircleIcon,
  WalletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { classicGames } from "../lib/classicGames";

const iconMap = {
  home: HomeIcon,
  casino: RectangleStackIcon,
  chart: ChartBarSquareIcon,
  ticket: TicketIcon,
  wallet: WalletIcon,
  gift: GiftIcon,
  user: UserCircleIcon,
  support: LifebuoyIcon,
};

export default function Sidebar({ isOpen, onClose, user }) {
  const [agentDoc, setAgentDoc] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setAgentDoc(null);
      return undefined;
    }

    return onSnapshot(doc(db, "agents", user.uid), (snapshot) => {
      setAgentDoc(
        snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null,
      );
    });
  }, [user?.uid]);

  const isApprovedAgent = agentDoc?.status === "approved";

  const sections = useMemo(() => {
    const base = [
      {
        title: "Main",
        items: [
          { label: "Home", icon: "home", to: "/" },
          { label: "Casino", icon: "casino", to: "/casino" },
          { label: "Crash", icon: "chart", to: "/crash" },
          { label: "Sports", icon: "chart", to: "/sports" },
        ],
      },
      {
        title: "Originals",
        items: [
          { label: "Limbo", icon: "casino", to: "/casino/limbo" },
          { label: "Dice Duel", icon: "casino", to: "/casino/dice-duel" },
          { label: "Coin Toss", icon: "casino", to: "/casino/coin-toss" },
          { label: "Lucky Wheel", icon: "gift", to: "/casino/lucky-wheel" },
          { label: "Mini Keno", icon: "ticket", to: "/casino/keno" },
        ],
      },
      {
        title: "Classic Games",
        items: classicGames.map((game) => ({
          label: game.title,
          icon: game.title.includes("Chess")
            ? "ticket"
            : game.title.includes("RPS")
              ? "gift"
              : "chart",
          to: game.to,
        })),
      },
      {
        title: "Finance",
        items: [
          { label: "Deposit", icon: "wallet", to: "/deposit" },
          { label: "Transactions", icon: "wallet", to: "/transactions" },
          { label: "Withdraw", icon: "wallet", to: "/withdraw" },
        ],
      },
      {
        title: "Lottery",
        items: [
          { label: "Lottery", icon: "ticket", to: "/agents" },
          { label: "My Tickets", icon: "ticket", to: "/my-tickets" },
          { label: "Winners", icon: "gift", to: "/winners" },
        ],
      },
      {
        title: "Support",
        items: [
          { label: "Profile", icon: "user", to: "/profile" },
          { label: "About", icon: "support", to: "/about" },
        ],
      },
    ];

    if (user?.uid) {
      base.splice(4, 0, {
        title: "Agent",
        items: isApprovedAgent
          ? [
              { label: "Agent Dashboard", icon: "chart", to: "/agent" },
              { label: "Payment Methods", icon: "wallet", to: "/agent/banks" },
              { label: "Card History", icon: "ticket", to: "/agent/history" },
            ]
          : [{ label: "Apply as Agent", icon: "user", to: "/agent/apply" }],
      });
    }

    return base;
  }, [isApprovedAgent, user?.uid]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/65 backdrop-blur-sm md:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        } transition-opacity`}
        onClick={onClose}
      />

      <aside
        className={`fixed left-0 top-0 z-50 flex h-[100dvh] w-[212px] flex-col border-r border-[#242a34] bg-[rgba(17,20,27,0.98)] px-3 py-4 backdrop-blur-xl transition-transform duration-300 md:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:static md:h-screen`}
      >
        <button
          className="absolute right-4 top-4 rounded-2xl bg-[#1d222b] p-2 text-slate-300 md:hidden"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="rounded-[22px] bg-[linear-gradient(180deg,#1c222c,#171b23)] p-3 shadow-[0_20px_60px_rgba(0,0,0,0.28)]">
          <div className="flex items-center gap-2">
            <img
              src="/assets/logo.svg"
              alt="IQ Cage"
              className="h-8 w-8 rounded-xl bg-[#10151c] p-1.5"
            />
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                Casino
              </div>
              <div className="text-xl font-black text-white">IQ CAGE</div>
            </div>
          </div>
          <div className="mt-3 rounded-[16px] bg-[linear-gradient(135deg,rgba(15,23,33,0.94),rgba(26,56,46,0.72))] px-3 py-3 text-[12px] leading-6 text-slate-200">
            Quick access to originals, live crash, and wallet actions.
          </div>
        </div>

        <nav className="mt-4 flex-1 overflow-y-auto pb-4">
          {sections.map((section) => (
            <div key={section.title} className="mt-4">
              <div className="px-2 py-2 text-[10px] uppercase tracking-[0.3em] text-slate-500">
                {section.title}
              </div>
              <ul className="space-y-2">
                {section.items.map((item) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <motion.li
                      key={item.to}
                      initial={false}
                      whileHover={{ x: 4 }}
                    >
                      <NavLink
                        to={item.to}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-[16px] px-4 py-2.5 text-[13px] font-semibold transition ${
                            isActive
                              ? "bg-[linear-gradient(135deg,rgba(23,198,135,0.2),rgba(18,32,28,0.96))] text-white shadow-[0_16px_36px_rgba(16,185,129,0.14)]"
                              : "bg-[#181d25] text-slate-300 hover:bg-[#1d232d] hover:text-white"
                          }`
                        }
                      >
                        <Icon className="h-5 w-5" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="rounded-[20px] bg-[#171c24] px-3 py-3 text-[11px] leading-6 text-slate-400">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
            Live Access
          </div>
          <div className="mt-2">
            Open crash, switch originals, or jump straight to wallet actions
            from one panel.
          </div>
        </div>
      </aside>
    </>
  );
}
